import { useState, useEffect } from "react";
import { NavLink, useParams } from "react-router";

import styled from "styled-components";
import { getServerById } from "../../../api/server";

import Button, { buttonClassName } from "../../../Components/Button";
import { Server } from "../../../types/server";

import ServerImage from "../../../assets/server.png";
import UserImage from "../../../assets/user.png";

export default function ServerView() {
  const { id } = useParams<{ id: string }>();
  const [server, setServer] = useState<Server | null>(null);

  useEffect(() => {
    if (!id) return console.error("No server id provided");
    getServerById(process.env.REACT_APP_BEARER, id).then((response) => {
      if (!response.success)
        return console.error("Error fetching server", response.error);

      setServer(response.data);
    });
  }, [id]);
  return !server ? <p>Loading</p> : <View server={server} />;
}

const Wrapper = styled.main`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  position: relative;
  padding: 28px 32px;
`;
const Header = styled(NavLink)`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 20px;
  & > div {
    padding: 0;
    width: 40px;
    box-shadow: none;
  }
`;
const Split = styled.div`
  display: grid;
  grid-template-columns: 6fr minmax(330px, 4fr);
  width: 100%;
  gap: 26px;
  margin-top: 26px;
  & > div {
    display: flex;
    flex-flow: column nowrap;
    position: relative;
  }
`;
const Jumbotron = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 18px;
  padding: 20px;
  background: #181818;
  border-radius: 12px;
  position: relative;
`;
const Slideshow = styled.figure`
  aspect-ratio: 780/380;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  & > p {
    color: rgba(255, 255, 255, 0.56);
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
  }
`;
const Dots = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 3px;
  & > span {
    height: 5px;
    width: 38px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    display: block;
  }
  & > span:first-child {
    background: #fff;
  }
`;
const Thumbs = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 10px;
  width: 100%;
  & > picture {
    opacity: 0.5;
    border-radius: 8px;
    height: 74px;
    flex-shrink: 0;
    flex-grow: 1;
    display: flex;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${ServerImage});
  }
  & > picture:first-child {
    opacity: 1;
  }
`;
const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;
const Divider = styled.div`
  width: 100%;
  height: 1px;
  flex-shrink: 0;
  margin-top: 24px;
  background: rgba(255, 255, 255, 0.1);
  display: block;
`;
const Aside = styled.aside`
  margin-top: 30px;
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  border-radius: 0 12px 12px 12px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 20px;
  & > h1 {
    font-size: 24px;
    font-weight: 600;
    line-height: 26px;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 16px;
  }
  & > ${Row} {
    gap: 14px;
  }
`;
const Tag = styled.div`
  height: 30px;
  flex-shrink: 0;
  position: absolute;
  top: -30px;
  left: 0;
  background: rgba(255, 255, 255, 0.32);
  color: #000;
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 20px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px 4px 0 0;
`;
const User = styled.div`
  margin-top: 14px;
  flex-shrink: 0;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 10px;
  position: relative;
  & > picture {
    height: 40px;
    flex-shrink: 0;
    width: 40px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${UserImage});
  }
  & > div {
    height: 100%;
    justify-content: space-between;
    display: flex;
    flex-flow: column nowrap;
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 20px;
    color: #fff;
  }
  & > div > p {
    color: rgba(255, 255, 255, 0.56);
  }
`;
const Buttons = styled.div`
  gap: 14px;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  & > button {
    box-shadow: none;
  }
`;
const Details = styled.div`
  margin-top: 20px;
  margin-bottom: 22px;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  gap: 10px;
  & > div {
    font-size: 15px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.56);
    line-height: 20px;
    text-transform: uppercase;
  }
`;
const Nav = styled.nav`
  margin-top: 20px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  margin-bottom: 1px;
  box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.1);
  & > button {
    padding: 0 20px;
    height: 60px;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.56);
    cursor: pointer;
  }
  & > button:disabled {
    cursor: default;
    color: #fff;
    box-shadow: inset 0 -2px 0 0 #fff;
  }
  & > button:not(:disabled):hover {
    color: rgba(255, 255, 255, 0.75);
    box-shadow: inset 0 -2px 0 0 rgba(255, 255, 255, 0.5);
  }
`;
const LongDescription = styled.p`
  margin-top: 20px;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.56);
`;
const Tags = styled.div`
  margin-top: 20px;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 10px;
  & > span {
    font-size: 15px;
    color: #fff;
    text-transform: uppercase;
    font-weight: 600;
    line-height: 20px;
  }
  & > ${Row} {
    gap: 10px;
  }
  & button {
    height: 36px;
    background: transparent;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    font-size: 15px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.56);
  }
`;

function View({ server }: { server: Server }) {
  return (
    <Wrapper>
      <Header to="/servers">
        <div className={buttonClassName("dark")}>&lt;</div>
        Back to servers
      </Header>
      <Split>
        <div>
          <Jumbotron>
            <Slideshow>
              <p>No Preview Images</p>
            </Slideshow>
            <Dots>
              <span />
              <span />
              <span />
            </Dots>
            <Thumbs>
              <picture />
              <picture />
              <picture />
              <picture />
            </Thumbs>
          </Jumbotron>
          <Nav>
            <button disabled>Description</button>
            <button>Required Packages</button>
            <button>Reviews</button>
          </Nav>
          <LongDescription>
            {server.description || "No description provided"}
          </LongDescription>
          <Tags>
            <span>Tags</span>
            <Row>
              {server.tags.map((tag, index) => (
                <Button key={index} variant="dark">
                  {tag}
                </Button>
              ))}
            </Row>
          </Tags>
        </div>
        <div>
          <Aside>
            <Tag>Server Details</Tag>
            <h1>{server.name}</h1>
            <Row>
              <span
                style={{
                  borderRadius: 4,
                  padding: "3px 6px",
                  height: 26,
                  lineHeight: "20px",
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#fff",
                  background: "rgba(255, 255, 255, 0.2)",
                }}
              >
                V{server.gameVersion}
              </span>
            </Row>
            <User>
              <picture />
              <div>
                conqr
                <p>Creator</p>
              </div>
            </User>
            <Row style={{ marginTop: 16, opacity: 0.5 }}>
              <Button variant="dark">0</Button>
              <Button variant="dark">0</Button>
            </Row>
            <Divider />
            <Details>
              <div>Players: {server.currentPlayers}</div>
              <div>Map: {server.map.name}</div>
              <div>Game Mode: {server.gameMode}</div>
              <div>Ping: 10ms</div>
            </Details>
            <Buttons>
              <Button>Join Server</Button>
              <Button variant="dark">Report Server</Button>
            </Buttons>
          </Aside>
        </div>
      </Split>
    </Wrapper>
  );
}
